import {
  Building2Icon,
  InboxIcon,
  RefreshCwIcon,
  SearchXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { Header } from "@/components/layout/header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { FRONTEND_ROUTES } from "@/constants"
import { useAuth } from "@/contexts/auth"
import {
  getWebhookInboxStatusClasses,
  todayRange,
  WebhookInboxDetailSheet,
  WebhookInboxFiltersBar,
} from "@/features/webhook-inbox"
import { cn } from "@/lib/utils"
import {
  useWebhookInbox,
  useWebhookInboxDetail,
} from "@/services/admin"
import type { WebhookInboxFilters } from "@/types/webhook-inbox"
import { DateHelper } from "@/utils"

const PAGE_SIZE = 20
const COLUMN_COUNT = 7
const SKELETON_ROWS = 8
const EM_DASH = "—"

/**
 * Recorte inicial: o dia corrente, como nas demais consultas do portal.
 *
 * É função, e não constante de módulo, para "hoje" ser resolvido a cada montagem e a cada
 * "Limpar" — como constante ele congelaria no carregamento do bundle, que numa SPA é ainda
 * mais cedo. Depois de montada a tela o valor segue fixo até o operador limpar o filtro.
 */
const initialFilters = (): WebhookInboxFilters => ({
  page: 0,
  size: PAGE_SIZE,
  ...todayRange(),
})

const formatDateTime = (value?: string) =>
  value
    ? DateHelper.formatDate(value, "dd/MM/yyyy HH:mm")
    : EM_DASH

/**
 * Webhook Inbox da operação: tudo que os processadores mandaram para a Pay2B,
 * com o desfecho do processamento e o caminho para o pedido correspondente.
 *
 * O recorte por white label vem do seletor GLOBAL do menu, como na consulta de templates: a
 * coluna de white label saiu da tabela porque repetia, linha a linha, o que a faixa no topo já
 * diz uma vez. No lugar dela entra o último erro — é o que se procura quando algo falhou.
 */
export function WebhookInboxPage() {
  const { t } = useTranslation()
  const { selectedWhiteLabel } = useAuth()
  const [filters, setFilters] =
    useState<WebhookInboxFilters>(initialFilters)
  const [selectedId, setSelectedId] = useState<
    string | undefined
  >()

  // O recorte por loja vem do seletor GLOBAL do menu — a tela não repete a
  // escolha num combo próprio.
  const query = useWebhookInbox({
    ...filters,
    whiteLabelId: selectedWhiteLabel?.uuid,
  })
  const { data, isLoading, isFetching, isError, refetch } =
    query
  const {
    data: detail,
    isLoading: isLoadingDetail,
    isError: isDetailError,
  } = useWebhookInboxDetail(selectedId)

  const items = data?.content ?? []
  const totalPages = data?.totalPages ?? 0
  const isBusy = isLoading || isFetching

  const handleFiltersChange = (
    partial: Partial<WebhookInboxFilters>
  ) =>
    // Filtro novo recomeça a paginação: manter a página anterior devolve
    // "nenhum resultado" quando o novo recorte é menor que o antigo.
    setFilters((current) => ({
      ...current,
      ...partial,
      page: 0,
    }))

  return (
    <>
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.DASHBOARD.href,
              label: t("sidebar.dashboard"),
            },
            { label: t("sidebar.administration") },
            { label: t("sidebar.webhookInbox") },
          ]}
        />
      </Header>
      <section className="flex flex-1 flex-col gap-4 p-4">
        <div>
          <h1 className="page-title">
            {t("pages.adminWebhookInbox.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("pages.adminWebhookInbox.subtitle")}
          </p>
        </div>

        {selectedWhiteLabel && (
          // Mesma faixa da consulta de templates: deixa explícito de qual white label são os
          // webhooks na tela, para não interpretar um problema como se fosse de outro.
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-l-4 border-amber-400 bg-amber-50 px-4 py-2 dark:border-amber-500/60 dark:bg-amber-950/30">
            <Building2Icon
              className="size-4 shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden="true"
            />
            <span className="text-sm text-amber-800 dark:text-amber-200">
              {t("pages.adminWebhookInbox.viewingFor")}
            </span>
            <span className="text-sm font-bold text-amber-900 dark:text-amber-100">
              {selectedWhiteLabel.name}
            </span>
          </div>
        )}

        <WebhookInboxFiltersBar
          filters={filters}
          onChange={handleFiltersChange}
          onClear={() => setFilters(initialFilters())}
          onRefresh={() => void refetch()}
        />

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t(
                    "pages.adminWebhookInbox.table.receivedAt"
                  )}
                </TableHead>
                <TableHead>
                  {t(
                    "pages.adminWebhookInbox.table.processor"
                  )}
                </TableHead>
                <TableHead className="hidden md:table-cell">
                  {t(
                    "pages.adminWebhookInbox.table.endpoint"
                  )}
                </TableHead>
                <TableHead>
                  {t(
                    "pages.adminWebhookInbox.table.status"
                  )}
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  {t(
                    "pages.adminWebhookInbox.detail.lastError"
                  )}
                </TableHead>
                <TableHead className="hidden text-center md:table-cell">
                  {t(
                    "pages.adminWebhookInbox.table.attempts"
                  )}
                </TableHead>
                <TableHead className="text-center">
                  {t("common.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isBusy &&
                Array.from({ length: SKELETON_ROWS }).map(
                  (_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={COLUMN_COUNT}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  )
                )}

              {!isBusy && isError && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <div className="flex flex-col items-center gap-3 py-10">
                      <TriangleAlertIcon
                        size={40}
                        strokeWidth={1.5}
                        className="text-red-600"
                      />
                      <p className="text-sm text-muted-foreground">
                        {t(
                          "pages.adminWebhookInbox.loadError"
                        )}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void refetch()}
                      >
                        <RefreshCwIcon
                          size={16}
                          className="mr-2"
                        />
                        {t("common.tryAgain")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {!isBusy &&
                !isError &&
                items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT}>
                      <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
                        <SearchXIcon
                          size={40}
                          strokeWidth={1.5}
                        />
                        <p>
                          {t(
                            "pages.adminWebhookInbox.empty"
                          )}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

              {!isBusy &&
                !isError &&
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatDateTime(item.receivedAt)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.processor
                        ? t(
                            `pages.adminWebhookInbox.processors.${item.processor}`
                          )
                        : EM_DASH}
                    </TableCell>
                    <TableCell className="hidden text-sm md:table-cell">
                      {item.endpoint
                        ? t(
                            `pages.adminWebhookInbox.endpoints.${item.endpoint}`
                          )
                        : EM_DASH}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="none"
                        className={cn(
                          "rounded-full border px-2 py-1 font-medium",
                          getWebhookInboxStatusClasses(
                            item.status
                          )
                        )}
                      >
                        {t(
                          `pages.adminWebhookInbox.status.${item.status}`
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden max-w-[280px] lg:table-cell">
                      {item.lastError ? (
                        <span
                          className="block truncate text-xs text-red-700 dark:text-red-400"
                          title={item.lastError}
                        >
                          {item.lastError}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {EM_DASH}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden text-center text-sm tabular-nums md:table-cell">
                      {item.attempts ?? 0}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSelectedId(item.id)
                        }
                      >
                        <InboxIcon
                          size={16}
                          className="mr-1"
                        />
                        {t("common.details")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2">
            <span className="text-sm text-muted-foreground">
              {t("pagination.page")} {filters.page + 1}{" "}
              {t("pagination.of")} {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={filters.page === 0}
              onClick={() =>
                setFilters((current) => ({
                  ...current,
                  page: current.page - 1,
                }))
              }
            >
              {t("common.previous")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={filters.page + 1 >= totalPages}
              onClick={() =>
                setFilters((current) => ({
                  ...current,
                  page: current.page + 1,
                }))
              }
            >
              {t("common.next")}
            </Button>
          </div>
        )}
      </section>

      <WebhookInboxDetailSheet
        detail={detail}
        isLoading={isLoadingDetail}
        isError={isDetailError}
        isOpen={Boolean(selectedId)}
        onOpenChange={(open) =>
          setSelectedId(open ? selectedId : undefined)
        }
      />
    </>
  )
}
